import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from 'react-bootstrap';
import { Typeahead, Menu, MenuItem, Highlighter } from 'react-bootstrap-typeahead';
import { format, sub, startOfMonth, addMonths } from 'date-fns';
import { DateFilter } from './components';
import IndividualDetailsLast12MonthsContainer from './container/IndividualDetailsLast12MonthsContainer';
import {
  GetFreemiumCorporateList,
  GetPaidCorporateList,
  clearKpiState,
  saveCropIdNameMap,
  saveFreemiumCropIdNameMap,
} from './store/actions';

import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-calendar/dist/Calendar.css';
import './assets/styles/CompanyKPIContainer.scss';
import IconArrowGround from './assets/images/icons/arrow-ground.svg';
import {
  downloadTableAsExcel,
  getDateFromString,
  getModifiedStartEndDate,
} from '../../utils/helpers';
import rowData from './constants/rowsForMonthlyData';
import ArrowBack from './assets/images/icons/arrow-back';
import { filterOptions, filterType } from '../../constants/filterOptions';
import IndividualDetailsLast90DaysContainer from './container/IndividualDetailsLast90DaysContainer';
import IndividualDetailsLast30DaysContainer from './container/IndividualDetailsLast30DaysContainer';

const IndividualDetailsContainer = () => {
  const DEFAULT_RANGE_MONTHS = 6;
  const now = new Date();
  const startDateDate = startOfMonth(addMonths(new Date(), -DEFAULT_RANGE_MONTHS));
  const endDateDate = sub(now, { days: 1 });

  const [startDate, setStartDate] = useState(format(startDateDate, 'LLL d, yyyy'));
  const [endDate, setEndDate] = useState(format(endDateDate, 'LLL d, yyyy'));
  const [startValue, setStartValue] = useState();
  const [endValue, setEndValue] = useState();

  const [companySelected, setCompanySelected] = useState([]);
  const auth = useSelector((state) => state.auth);
  const corporateList = useSelector((state) => state.kpi.corporateList);
  const freemiumCorporateList = useSelector((state) => state.kpi.freemiumCorporateList);
  const individualDetails = useSelector((state) => state.kpi.individualDetails);
  const [copyCorporateList, setCopyCorporateList] = useState([]);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [currentView, setCurrentView] = useState(filterType.custom);
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const payloadRef = useRef({});
  const navigate = useNavigate();
  const { state } = useLocation();
  const field = searchParams.get('field');

  const dateRangeForIndividualDetails =
    individualDetails.currentDateRange?.[searchParams.get('field')];

  function downloadSheet() {
    const tableElement = document.getElementById('data-table-container');
    const name = rowData.find((data) => data.key === searchParams.get('field'))?.title;
    downloadTableAsExcel(tableElement, `${name}.xlsx`);
  }

  useEffect(() => {
    // if (!corporateList.data || corporateList.data.length === 0) {
      dispatch(GetPaidCorporateList({ viewedFrom: 'kpi' }));
    // }
    //if (!freemiumCorporateList.data || freemiumCorporateList.data.length === 0) {
    dispatch(GetFreemiumCorporateList({ viewedFrom: 'kpi' }));
    //}
  }, []);

  useEffect(() => {
    if (state && state.filterType && state.startDate && state.endDate) {
      setCurrentView(state.filterType);
      setStartDate(format(getDateFromString(state.startDate), 'LLL d, yyyy'));
      setEndDate(format(getDateFromString(state.endDate), 'LLL d, yyyy'));
      setStartValue(getDateFromString(state.startDate));
      setEndValue(getDateFromString(state.endDate));
    }
  }, [state]);

  useEffect(() => {
    if (
      (!state || !state.filterType || !state.startDate || !state.endDate) &&
      (!startValue || !endValue)
    ) {
      setStartValue(startDateDate);
      setEndValue(endDateDate);
      setCurrentView(filterType.custom);
    }
  }, [state, startValue, endValue, dateRangeForIndividualDetails]);

  useEffect(() => {
    if (
      corporateList &&
      Array.isArray(corporateList.data) &&
      corporateList.data.length >= 1 &&
      companySelected.length === 0
    ) {
      let tmpCorporateList = [...corporateList.data];
      const ownCompany = corporateList.data.find((a) => a._id === auth.data.corporateId);
      if (ownCompany) {
        // If company owner is Wellness Coach, then show All Paid Company as company to get summary of all companies
        if (ownCompany.entity_name === 'Wellness Coach') {
          setCompanySelected([{ _id: 'ALL', entity_name: 'All Paid Company', status: 1 }]);
          tmpCorporateList.unshift({ _id: 'ALL', entity_name: 'All Paid Company', status: 1 });
        } else {
          setCompanySelected([ownCompany]);
        }
      } else {
        setCompanySelected(corporateList.data.slice(0, 1));
      }
      setCopyCorporateList(tmpCorporateList);
      const corpIdNameMap = {};
      tmpCorporateList.forEach((c) => {
        corpIdNameMap[c._id] = c.entity_name;
      });
      dispatch(saveCropIdNameMap(corpIdNameMap));
    }
  }, [corporateList]);

  useEffect(() => {
    if (
      freemiumCorporateList &&
      Array.isArray(freemiumCorporateList.data) &&
      freemiumCorporateList.data.length >= 1
    ) {
      const corpIdNameMap = {};
      freemiumCorporateList.data.forEach((c) => {
        corpIdNameMap[c._id] = c.entity_name;
      });
      dispatch(saveFreemiumCropIdNameMap(corpIdNameMap));
    }
  }, [freemiumCorporateList]);

  function getCorporateLogo(companySelected) {
    if (!companySelected[0]) {
      return '';
    }
    if (!companySelected[0]?.company_logo) {
      return (
        <div className='company-report--header--logo--no-logo-just-first-letter'>
          {companySelected[0].entity_name.charAt(0)}
        </div>
      );
    }
    return companySelected && companySelected[0]?.company_logo ? (
      <img src={companySelected[0].company_logo} alt='' />
    ) : (
      ''
    );
  }

  const onClickDateFilter = (e) => {
    e.stopPropagation();
    setShowDateFilter(!showDateFilter);
  };

  const onFilterUpdate = (selectedFilter, sDate, eDate) => {
    setStartDate(format(sDate, 'LLL d, yyyy'));
    setStartValue(sDate);
    setEndDate(format(eDate, 'LLL d, yyyy'));
    setEndValue(eDate);
    setCurrentView(selectedFilter.value);
  };

  const onClearKpiState = () => {
    dispatch(clearKpiState());
    setCompanySelected([]);
    dispatch(GetPaidCorporateList({ viewedFrom: 'kpi' }));
    dispatch(GetFreemiumCorporateList({ viewedFrom: 'kpi' }));
  }

  return (
    <div className='container-fluid kpi'>
      <div className='company-report'>
        <div className='company-report--actions'>
          <div className='company-report--header'>
            <div className='company-report--header--logo'>{getCorporateLogo(companySelected)}</div>
            <div className='company-report--header--title'>
              <Typeahead
                id='some_id'
                onChange={(selected) => {
                  setCompanySelected(selected);
                }}
                renderMenu={(results, menuProps) => {
                  let listWithActive = !copyCorporateList
                    ? []
                    : copyCorporateList.filter((c) => c.status === 1);

                  const results_len = results.length;
                  return (
                    <Menu {...menuProps} emptyLabel='No results found'>
                      {results.map((result, index) => (
                        <MenuItem option={result} position={index} key={index}>
                          <Highlighter search={menuProps.text}>{result.entity_name}</Highlighter>
                        </MenuItem>
                      ))}
                      {results_len > 0 && <Menu.Divider />}
                      {listWithActive.slice(0, 30).map(
                        (result, index) =>
                          result?._id !== companySelected[0]?._id && (
                            <MenuItem
                              option={result}
                              position={index + results_len + 1}
                              key={index + results_len + 1}
                            >
                              {result.entity_name}
                            </MenuItem>
                          )
                      )}
                      {listWithActive.length > 30 ? 'Please type name to see more ... ' : ''}
                    </Menu>
                  );
                }}
                className={
                  copyCorporateList && copyCorporateList.length === 1
                    ? 'company-report--header--single'
                    : ''
                }
                filterBy={['entity_name']}
                labelKey={'entity_name'}
                options={copyCorporateList ? copyCorporateList.filter((c) => c.status === 1) : []}
                selected={companySelected}
              />
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <div className='company-report--actions--date'>
              <div className='company-report--actions--date--wrapper' onClick={onClickDateFilter}>
                <Badge className='current-filter' bg='light' text='dark'>
                  {filterOptions.find((option) => option.value === currentView)?.name || 'Custom'}
                </Badge>
                {startValue && endValue && (
                  <span className='date-range'>
                    {format(getDateFromString(startValue), 'MMM d')} -{' '}
                    {format(getDateFromString(endValue), 'MMM d, yyyy')}
                  </span>
                )}
                <span className='select-icon-down' aria-hidden='true'></span>
              </div>
              {showDateFilter && (
                <DateFilter
                  onClose={() => setShowDateFilter(false)}
                  startValue={startValue}
                  endValue={endValue}
                  onFilterUpdate={onFilterUpdate}
                  selectedFilter={dateRangeForIndividualDetails?.selectedFilter}
                />
              )}
            </div>
            <div className={`company-report--actions--download`} onClick={() => downloadSheet()}>
              <img src={IconArrowGround} alt='' />
              <span>Download</span>
            </div>
          </div>
        </div>
        <div className='d-flex align-items-center gap-1 ms-2'>
          <ArrowBack onClick={() => navigate(-1)} />
          <h4>{rowData.find((data) => data.key === searchParams.get('field'))?.title}</h4>
        </div>
        <div id='data-table-container' className='data-table-container table-responsive'>
          {[filterType.custom, filterType.last12Months].includes(currentView) && (
            <IndividualDetailsLast12MonthsContainer
              field={field}
              companySelected={companySelected}
              startValue={startValue}
              endValue={endValue}
              payload={payloadRef.current ?? {}}
              selectedFilteredType={currentView}
              onClearKpiState={onClearKpiState}
            />
          )}
          {currentView === filterType.last90Days && (
            <IndividualDetailsLast90DaysContainer
              field={field}
              companySelected={companySelected}
              startValue={startValue}
              endValue={endValue}
              payload={payloadRef.current ?? {}}
              onClearKpiState={onClearKpiState}
            />
          )}
          {currentView === filterType.last30Days && (
            <IndividualDetailsLast30DaysContainer
              field={field}
              companySelected={companySelected}
              startValue={startValue}
              endValue={endValue}
              payload={payloadRef.current ?? {}}
              onClearKpiState={onClearKpiState}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IndividualDetailsContainer;
